import { Link, useNavigate, useParams } from "react-router-dom";
import { useGetMyDietLogbooksQuery } from "../api/logbookDietApi";

type MyDietLogbooksResponse = {
  diet_id?: string;
  member_id?: string;
  assessor_id?: string;
  total?: number;
  logbooks?: any[];
};

function safeMsg(x: any) {
  return String(
    x?.data?.message ||
      x?.error ||
      x?.message ||
      x?.data?.error ||
      "Failed to load logbooks."
  );
}

export default function MyDietLogbooksPage() {
  const navigate = useNavigate();
  const { id = "" } = useParams();

  const q = useGetMyDietLogbooksQuery(id, { skip: !id }) as any;

  const data = (q.data as MyDietLogbooksResponse | undefined) ?? undefined;
  const isLoading = Boolean(q.isLoading);
  const isError = Boolean(q.isError);
  const error = q.error;
  const refetch = q.refetch as () => any;

  const logbooks = data?.logbooks ?? [];

  return (
    <div className="bg-white border rounded-xl p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <button
            onClick={() => navigate("/assessor/logbook-diets")}
            className="text-sm text-gray-600 hover:underline"
          >
            ← Back to My Diets
          </button>
          <h1 className="text-xl font-bold text-gray-800 mt-2">
            My Diet Logbooks
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Diet: {data?.diet_id ?? id}
          </p>
        </div>

        <button
          onClick={() => refetch()}
          className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
        >
          Refresh
        </button>
      </div>

      {isLoading ? (
        <div className="mt-6 text-gray-600">Loading logbooks...</div>
      ) : isError ? (
        <div className="mt-6 text-red-600">{safeMsg(error)}</div>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Logbook ID</th>
                <th className="py-2">Diet</th>
                <th className="py-2">Stage</th>
                <th className="py-2">Status</th>
                <th className="py-2">Score</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>

            <tbody>
              {logbooks.map((lb: any) => (
                <tr key={lb.id} className="border-b">
                  <td className="py-3 font-semibold text-gray-800">{lb.id}</td>
                  <td className="py-3 text-gray-600">
                    {lb?.diet?.title ?? "—"}
                  </td>
                  <td className="py-3 text-gray-600">{lb?.stage ?? "—"}</td>
                  <td className="py-3 text-gray-600">{lb?.status ?? "—"}</td>
                  <td className="py-3 text-gray-600">
                    {lb?.total_score ?? "—"}
                  </td>

                  <td className="py-3">
                    <Link
                      to={`/assessor/logbook/${lb.id}`}
                      className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                    >
                      View Details
                    </Link>
                  </td>
                </tr>
              ))}

              {!logbooks.length ? (
                <tr>
                  <td colSpan={6} className="py-6 text-gray-500">
                    No logbooks found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
